import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography, Alert, Button } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useAuth } from "../context/AuthContext";
import { defaultAppPath } from "../utils/entitlements";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

function decodeTokenRoles(accessToken) {
  try {
    const payload = JSON.parse(
      atob(accessToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return payload?.realm_access?.roles || [];
  } catch {
    return [];
  }
}

/**
 * SSO Callback Handler
 *
 * This page is loaded after Keycloak redirects back from Microsoft SSO.
 * It receives ?code=...&state=... in the URL and exchanges the code for tokens.
 *
 * Flow:
 *   1. Validate state (CSRF protection)
 *   2. Send code to backend /api/sso-exchange
 *   3. Backend exchanges code for token, checks @qualizeal.com domain,
 *      assigns platform-admin role, returns tokens
 *   4. Store tokens in AuthContext, redirect to /platform/tenants
 */
export default function SSOCallback() {
  const navigate = useNavigate();
  const { injectTokens } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallback = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const errorParam = params.get("error");
      const errorDesc = params.get("error_description");

      // Handle error from Keycloak/Microsoft
      if (errorParam) {
        setError(errorDesc || errorParam);
        setLoading(false);
        return;
      }

      if (!code) {
        setError("No authorization code received from SSO provider.");
        setLoading(false);
        return;
      }

      // CSRF: verify state matches what we sent
      const expectedState = sessionStorage.getItem("sso_state");
      if (!expectedState || state !== expectedState) {
        setError("Invalid SSO state. Please try logging in again.");
        setLoading(false);
        return;
      }

      const realm = sessionStorage.getItem("sso_realm") || "validaite";
      sessionStorage.removeItem("sso_state");
      sessionStorage.removeItem("sso_realm");

      // Exchange code for tokens via backend
      const redirectUri = `${window.location.origin}/sso-callback`;
      const res = await fetch(`${BACKEND}/api/sso-exchange`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, redirectUri, realm }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || "SSO login failed.");
        setLoading(false);
        return;
      }

      // Store tokens in AuthContext
      injectTokens({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        id_token: data.id_token,
      });

      const roles = decodeTokenRoles(data.access_token);
      navigate(defaultAppPath(roles), { replace: true });
    } catch (err) {
      console.error("SSO callback error:", err);
      setError("Network error during SSO login. Please try again.");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        height: "100vh", bgcolor: "#F9FAFB", gap: 2,
      }}>
        <CircularProgress size={40} sx={{ color: "#1D4ED8" }} />
        <Typography sx={{ fontSize: "15px", color: "#374151", fontWeight: 500 }}>
          Signing you in...
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#9CA3AF" }}>
          Please wait, this should only take a moment.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      height: "100vh", bgcolor: "#F9FAFB", px: 3,
    }}>
      <Box sx={{ maxWidth: 440, width: "100%", textAlign: "center" }}>
        <ErrorOutlineIcon sx={{ fontSize: 56, color: "#DC2626", mb: 2 }} />
        <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#111827", mb: 1 }}>
          Sign-in failed
        </Typography>
        <Alert severity="error" sx={{ textAlign: "left", fontSize: "13px", borderRadius: "8px", mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/", { replace: true })}
          sx={{
            bgcolor: "#1D4ED8", textTransform: "none", fontWeight: 600, px: 4, py: 1.2,
            borderRadius: "6px", "&:hover": { bgcolor: "#1E40AF" },
          }}
        >
          Back to Login
        </Button>
      </Box>
    </Box>
  );
}
